import { User } from '@sr-sdks/api-sdk-axios';
import { Button, sonner } from '@sr/ui-library';
import { useEffect, useState } from 'react';
import { ChangePasswordModal } from '../../../components/AccountSettings/ChangePasswordModal';
import { InputWrapper } from '../../../components/InputWrapper';
import { configuration } from '../../../configuration';
import { userService } from '../../../reducers/permissions/userSlice';
import { useAppDispatch, useTypedSelector } from '../../../stateStore';
import { isLoadingState, isSuccessState } from '../../../utils/helpers/common';
import { MSR3ProfileSettingsForm } from './MSR3ProfileSettingsForm';

const { toast } = sonner;

export const MSR3ProfileSettings = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const [isChangePasswordButtonDisabled, setIsChangePasswordButtonDisabled] =
    useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);

  const userMe = useTypedSelector(userService.selectors.userMe);
  const isPasswordChanging = useTypedSelector(
    userService.selectors.isPasswordChanging,
  );

  const handleSaveSuccess = async () => {
    await dispatch(userService.findMe());
    toast.success('Your profile has been updated.');
  };

  const handleChangePassword = async (user?: User) => {
    // Cognito uses the in-app modal (APP-1896); Auth0 uses the email reset.
    if (configuration.COGNITO_ENABLED) {
      setIsChangePasswordModalOpen(true);

      return;
    }

    if (!user?.emailAddress) return;

    await dispatch(userService.changePassword(user.emailAddress));
  };

  useEffect(() => {
    // Only the Auth0 path produces the "we've sent you an email" toast.
    // The Cognito path lives entirely inside the modal.
    if (configuration.COGNITO_ENABLED) return;

    if (isSuccessState(isPasswordChanging)) {
      toast.success("We've sent you an email to change your password.", {
        description:
          "Please check your inbox and follow the instructions. If you can't see the email, try checking your spam folder.",
        onAutoClose: () =>
          dispatch(userService.actions.resetPasswordLoadingState()),
      });

      setIsChangePasswordButtonDisabled(true);

      return () => {
        dispatch(userService.actions.resetPasswordLoadingState());
      };
    }
  }, [dispatch, isPasswordChanging]);

  return (
    <div className="flex w-full flex-col items-start gap-5 rounded-lg border border-sr-outline bg-white p-4 shadow-sm sm:p-6">
      <MSR3ProfileSettingsForm onSaveSuccess={handleSaveSuccess} />

      <div className="flex flex-col items-start gap-2.5 pt-1">
        <InputWrapper label="Password" isFieldset>
          <Button
            className="mt-2.5"
            disabled={
              !configuration.COGNITO_ENABLED && isChangePasswordButtonDisabled
            }
            loading={
              !configuration.COGNITO_ENABLED &&
              isLoadingState(isPasswordChanging)
            }
            size="sm"
            onClick={() => handleChangePassword(userMe)}
          >
            Change Password
          </Button>
        </InputWrapper>
      </div>

      {configuration.COGNITO_ENABLED && (
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={() => setIsChangePasswordModalOpen(false)}
        />
      )}
    </div>
  );
};
